/**
 * Every judgement the approvals screen makes, and every sentence it says.
 *
 * This workspace ships no DOM test environment, so anything decided inside JSX
 * is decided where nothing can check it. On most screens that costs a wrong
 * word. On this one it can cost a message: the queue is worked from the
 * keyboard, one draft at a time, and pressing `a` sends what is in front of
 * you. So the two things this file exists to make impossible are both about
 * that:
 *
 *   - A draft that is on screen and a draft the address bar names must be the
 *     same draft, always. The page used to fall back to the first item whenever
 *     the requested one was missing, which meant a filter change, a background
 *     refetch, or a colleague deciding a draft first could swap the pane under
 *     somebody's hands while the URL still claimed the draft they opened. The
 *     next `a` then approved a message they had never read. There is no
 *     substitution here. A draft that is not in the visible list produces a
 *     stated reason instead of a different draft.
 *   - "Nothing left to approve" and "nothing on the campaign you picked" are
 *     opposite facts wearing the same empty box. One means you are finished,
 *     the other means you are looking in the wrong place and work is piling up
 *     out of sight. They get different words, in the list and in the pane.
 *
 * The filter itself narrows the queue to one campaign rather than hiding one.
 * A reviewer working the queue wants to finish a campaign, and picking one is
 * also something you can hand to somebody else as a link. "Everything except
 * this one" is not a sentence anybody sends.
 *
 * That narrowing happens inside the server's query, before the queue is cut
 * into a page, so the list arrives holding the picked campaign and nothing here
 * sieves it again. The option list and its counts arrive with it, over the
 * whole queue rather than over the page. That is the difference between "Ops
 * hiring (25)" meaning twenty five people are waiting and it meaning twenty
 * five rows happened to fit. The pager at the foot of the rail says which rows
 * of that campaign are on screen, and gets to the rest of them, which is what
 * the sentence that used to sit there could only describe.
 *
 * What a narrowed list costs is the ability to tell a draft on another campaign
 * apart from a draft that has left the queue, because neither of them is on the
 * page. That is answered by saying only what is known rather than by guessing
 * at the likelier half. See `readSelection`.
 *
 * The next section is the editor itself. A device DM goes out as a short burst
 * of messages rather than one block, so the edit buffer is a list of messages
 * and every gesture that changes it lives here as a plain function. Same reason
 * as the rest of the file: those gestures decide what a person sends, and inside
 * JSX nothing can check them.
 *
 * The last section is the state this queue never used to have. A message that
 * was approved, was handed over to be sent and never arrived comes back here
 * rather than to a screen of its own, because this is already the place a person
 * looks at one message and decides what happens to it, and a second surface for
 * the same job is the one nobody opens. What it needs is a different decision
 * from the rest of the queue, so it gets a different control, and the words for
 * both of them live down there where they can be swept.
 *
 * Copy rules, swept by the test next door: no em dashes, no semicolons, no
 * words out of the data model, and no spec identifiers.
 */
import { joinMessages, splitIntoMessages } from '@gojibasel/shared/burst'
import { detectTextLanguage } from '@gojibasel/shared/locale'
import { channelSendsBursts } from '@gojibasel/shared/types'
import { formatNumber, plural } from '../../lib/format'
import type { Tone } from '../../lib/labels'
import type { ActionType, ApprovalDelivery, ApprovalItem, Channel, Uuid } from '../../lib/types'

// ---------------------------------------------------------------------------
// What the screen says about itself
// ---------------------------------------------------------------------------

export const PAGE_TITLE = 'Approvals'
/**
 * The last sentence is there because the queue holds two kinds of thing now, and
 * a screen that names only the commoner one leaves the other reading as a bug
 * the first time somebody meets it.
 */
export const PAGE_DESCRIPTION =
  'Nothing goes out until someone here says so. Each draft comes with the case for contacting this person, so you are judging the decision and not only the wording. A message that was approved and then did not send comes back to this queue as well, so it is decided in the same place.'
export const REFRESH_LABEL = 'Refresh'
export const QUEUE_CARD_TITLE = 'Waiting on you'

/** The running tally in the header, and null before the first decision. */
export function decidedNote(count: number): string | null {
  if (count <= 0) return null
  return `${formatNumber(count)} decided in this session`
}

/** The badge over the list. Always about what is in front of somebody, never about the queue. */
export function queueCountLabel(shown: number): string {
  return plural(shown, 'draft')
}

// ---------------------------------------------------------------------------
// The campaign filter, as it travels in the address bar
// ---------------------------------------------------------------------------

export interface ApprovalFilters {
  /** One campaign, or '' for the whole queue. */
  campaignId: Uuid | ''
  /**
   * Only rows in one state, as the status pill names them, or '' for every
   * state. The server sieves by the same readers the pill does. Optional so a
   * caller written before it keeps building.
   */
  status?: ApprovalStatusFilter | ''
  /** Only rows holding every word of this: the recipient, the sequence and the words. */
  q?: string
}

/** The states the Status filter offers, as the server's `status` parameter names them. */
export type ApprovalStatusFilter = 'waiting' | 'needs_writing' | 'held' | 'not_sent'

/** In the order the status pill ranks them, worded as the pill words them. */
export const APPROVAL_STATUS_CHOICES: readonly { value: ApprovalStatusFilter; label: string }[] = [
  { value: 'waiting', label: 'Waiting' },
  { value: 'needs_writing', label: 'Needs writing' },
  { value: 'held', label: 'Held' },
  { value: 'not_sent', label: 'Did not send' },
]

export const STATUS_PARAM = 'status'
export const SEARCH_PARAM = 'q'

/** The longest search the server takes. */
export const SEARCH_MAX_LENGTH = 200

/** Whether the status or the search narrows the queue, apart from the campaign. */
export function sieveActive(filters: ApprovalFilters): boolean {
  return (filters.status ?? '') !== '' || (filters.q ?? '').trim() !== ''
}

export const EMPTY_APPROVAL_FILTERS: ApprovalFilters = { campaignId: '' }

/**
 * The parameters this screen owns.
 *
 * `draft` is one of them even though it is not a filter, because picking a
 * campaign has to clear it in the same write. Anything else in the query string
 * is left alone, so a parameter another feature adds later survives a filter
 * change instead of being swept up.
 */
export const CAMPAIGN_PARAM = 'campaign'
export const DRAFT_PARAM = 'draft'
export const APPROVAL_PARAMS = [CAMPAIGN_PARAM, DRAFT_PARAM] as const

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * The campaign a link asks for, or nothing.
 *
 * Strict on shape and deliberately loose on existence. A well formed id for a
 * campaign with nothing pending survives, so a link to an emptied campaign
 * shows the named control over the filtered-to-nothing panel rather than
 * silently showing the whole queue and leaving somebody to work out that the
 * link they were sent did not do what it said. A mangled id is dropped, because
 * there is nothing true to say about it, and because it is what the request
 * would be refused for anyway.
 */
export function readCampaignParam(raw: string | null): Uuid | '' {
  if (raw === null) return ''
  const value = raw.trim()
  return UUID.test(value) ? value : ''
}

/** The filters a query string asks for, with anything unusable dropped. */
export function readApprovalFilters(params: URLSearchParams): ApprovalFilters {
  const status = APPROVAL_STATUS_CHOICES.find((choice) => choice.value === params.get(STATUS_PARAM))?.value ?? ''
  const q = (params.get(SEARCH_PARAM) ?? '').trim()
  return {
    campaignId: readCampaignParam(params.get(CAMPAIGN_PARAM)),
    status,
    // Dropped rather than cut when it is longer than the server takes, so a
    // link never asks for a search it did not mean.
    q: q.length > SEARCH_MAX_LENGTH ? '' : q,
  }
}

/**
 * The query string after the status filter changed. It shuts the drawer in
 * the same write, for the reason picking a campaign does: the draft open may
 * not be in the narrowed list, and the drawer sends what it holds.
 */
export function pickStatusParams(status: ApprovalStatusFilter | '', current: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(current)
  if (status === '') next.delete(STATUS_PARAM)
  else next.set(STATUS_PARAM, status)
  next.delete(DRAFT_PARAM)
  return next
}

/**
 * The query string after the search changed. The drawer stays: a draft the new
 * list does not hold is said to be missing by `readSelection`, never replaced.
 */
export function searchParams(q: string, current: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(current)
  const value = q.trim()
  if (value === '' || value.length > SEARCH_MAX_LENGTH) next.delete(SEARCH_PARAM)
  else next.set(SEARCH_PARAM, value)
  return next
}

/** Every filter off, the campaign included, and the drawer shut with them. */
export function clearAllFilterParams(current: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(current)
  next.delete(CAMPAIGN_PARAM)
  next.delete(STATUS_PARAM)
  next.delete(SEARCH_PARAM)
  next.delete(DRAFT_PARAM)
  return next
}

/** The draft a link asks for, or null. Same shape rule, same reason. */
export function readDraftParam(params: URLSearchParams): Uuid | null {
  const raw = params.get(DRAFT_PARAM)
  if (raw === null) return null
  const value = raw.trim()
  return UUID.test(value) ? value : null
}

export function anyApprovalFilterActive(filters: ApprovalFilters): boolean {
  return filters.campaignId !== '' || sieveActive(filters)
}

/**
 * The query string for picking a campaign, over whatever is already there.
 *
 * The open draft is dropped in the same write, and that is the whole point of
 * this function existing rather than the page setting a parameter inline. Two
 * writes would leave a frame in which the address bar names a campaign and a
 * draft that is not on it, and this screen sends what it has selected. One
 * write means the two can never disagree.
 *
 * An empty pick removes the parameter rather than writing an empty value, so no
 * filter produces a bare URL and two identical picks produce one identical
 * string. That is what lets a caller tell a real change from a repaint, and it
 * is now also what tells the cache one campaign from another.
 *
 * The value goes through the same reader a link goes through, so this can never
 * write something the reader would drop. A link carrying a filter the page will
 * not honour is worse than one carrying no filter, because the second at least
 * looks like what it does.
 */
export function pickCampaignParams(campaignId: string, current: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(current)
  const value = readCampaignParam(campaignId)
  if (value === '') next.delete(CAMPAIGN_PARAM)
  else next.set(CAMPAIGN_PARAM, value)
  next.delete(DRAFT_PARAM)
  return next
}

/** The query string for opening a draft, or for having none open. */
export function selectDraftParams(draftId: string | null, current: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(current)
  if (draftId === null || draftId.trim() === '') next.delete(DRAFT_PARAM)
  else next.set(DRAFT_PARAM, draftId.trim())
  return next
}

// ---------------------------------------------------------------------------
// The control, and what picking an option would give
// ---------------------------------------------------------------------------

export const CAMPAIGN_FILTER_LABEL = 'Sequence'
export const EVERY_CAMPAIGN_OPTION = 'Every sequence'
export const CLEAR_FILTER_LABEL = 'Show every sequence'

/**
 * A campaign that arrived with no name on it.
 *
 * The name is read in a second join on the server, so it can come back blank
 * for a campaign renamed to nothing while drafts of its own are still queued.
 * Better a plain option than a blank one.
 */
export const UNNAMED_CAMPAIGN_OPTION = 'A sequence with no name on it'

/**
 * The campaign a link points at, when nothing is waiting on it.
 *
 * The response lists the campaigns with something in the queue, so a campaign
 * that has just been emptied is not among them and has no name to show.
 * Somebody arriving on a filtered link is looking at a narrowed queue, and the
 * control is the only thing on screen that says which campaign that is. A blank
 * control over an empty queue reads as a broken control.
 */
export const LINKED_CAMPAIGN_OPTION = 'The sequence this link points at'

/**
 * One campaign with drafts waiting, as the queue reports it.
 *
 * `pending` is over the whole queue and not over the page in front of somebody.
 * The two used to be the same number by construction, because the screen
 * counted the rows it held. They are not the same question, and a fifty row
 * page over a sixty draft campaign is where they part.
 */
export interface CampaignCount {
  id: string
  name: string
  pending: number
}

export interface CampaignOption {
  value: string
  /** The words and the count together, "Spring outreach (7)". */
  label: string
  count: number
}

/**
 * How many campaigns have to be in the queue before the control shows: one.
 *
 * It used to wait for a second campaign, on the grounds that with one there is
 * no choice to make. On 2026-09-14 the owner asked for a campaign filter on this
 * page and could not find it, because FirmAdapt's queue held a single campaign,
 * so the control was hidden. A filter that disappears exactly when somebody
 * looks for it reads as missing. It shows whenever anything is waiting, and it
 * stays whenever a campaign is picked, so the pick is always visible and always
 * reversible.
 */
export const CAMPAIGNS_BEFORE_FILTER = 1

/** Everything waiting on somebody, across every campaign in the queue. */
export function totalWaiting(campaigns: readonly CampaignCount[]): number {
  return campaigns.reduce((sum, campaign) => sum + campaign.pending, 0)
}

/**
 * The options for the campaign control, or null when there is nothing to
 * choose.
 *
 * This reads the campaigns the response carries and never the rows on screen,
 * and that is the change worth stating. Counting the rows made every number
 * here a count of what fitted on a page: a campaign with thirty waiting behind
 * a fifty row page shared by two others was offered as twenty five, and a
 * campaign whose drafts all sat past the page boundary was not offered at all.
 * The response counts the queue, so a number beside a name is the number of
 * people waiting on that campaign.
 *
 * That number can be larger than the page picking it will show. The pager
 * covers the difference, and the alternative is a control that goes quiet about
 * work it can see.
 *
 * The list is the same list whichever campaign is picked, so a narrowed queue
 * still offers the campaigns it is not showing. A pick with nothing waiting
 * behind it is not in that list and is added back, so a link into an emptied
 * campaign names it rather than showing blank.
 */
export function campaignOptions(
  campaigns: readonly CampaignCount[],
  filters: ApprovalFilters,
): CampaignOption[] | null {
  const picked = filters.campaignId
  if (campaigns.length < CAMPAIGNS_BEFORE_FILTER && picked === '') return null

  const named = campaigns.map((campaign) => ({
    id: campaign.id,
    name: campaign.name.trim() === '' ? UNNAMED_CAMPAIGN_OPTION : campaign.name,
    count: campaign.pending,
  }))
  if (picked !== '' && !campaigns.some((campaign) => campaign.id === picked)) {
    named.push({ id: picked, name: LINKED_CAMPAIGN_OPTION, count: 0 })
  }

  // Alphabetical, because somebody is looking for a campaign by its name rather
  // than reading a ranking. Ties fall back to the id so the order never shifts
  // between renders, and sorting here rather than trusting the order it arrived
  // in keeps the campaign that was added back in its place among the rest.
  named.sort((left, right) => {
    const words = left.name.localeCompare(right.name, undefined, { sensitivity: 'base' })
    return words !== 0 ? words : left.id.localeCompare(right.id)
  })

  const everything = totalWaiting(campaigns)
  return [
    { value: '', label: `${EVERY_CAMPAIGN_OPTION} (${formatNumber(everything)})`, count: everything },
    ...named.map((entry) => ({
      value: entry.id,
      label: `${entry.name} (${formatNumber(entry.count)})`,
      count: entry.count,
    })),
  ]
}

// ---------------------------------------------------------------------------
// What is on screen and what is not
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// The empty queue, and the empty view of a queue that is not empty
// ---------------------------------------------------------------------------

export const CAUGHT_UP_TITLE = 'You are all caught up'
export const CAUGHT_UP_DESCRIPTION =
  'Drafts land here as sequence steps come due. If a sequence is running and nothing has appeared, it is most likely waiting on its working hours or on a daily ceiling, and both clear on their own.'
export const CAUGHT_UP_LINE = 'Nothing waiting.'

export const NO_MATCH_TITLE = 'Nothing on that sequence right now'
export const NO_MATCH_DESCRIPTION =
  'The rest of the queue is still waiting on you. Show every sequence to get back to it.'
export const NO_MATCH_LINE = 'Nothing on that sequence.'

export const NO_SIEVE_MATCH_TITLE = 'No drafts match'
export const NO_SIEVE_MATCH_DESCRIPTION =
  'Nothing waiting matches the search and filters. The rest of the queue is still there. Clear them to get back to it.'
export const NO_SIEVE_MATCH_LINE = 'Nothing matches.'
export const CLEAR_ALL_FILTERS_LABEL = 'Clear filters'

export interface QueueEmptyReading {
  title: string
  description: string
  /** The compact version, for the narrow list beside the draft. */
  line: string
  /** Whether the way out of this is clearing the filter. */
  clearable: boolean
}

/**
 * Which empty queue somebody is looking at.
 *
 * These must never collapse into one panel. "You are all caught up" ends
 * somebody's shift. "Nothing on that campaign" means drafts are stacking up
 * behind a control they forgot they set, and the difference between the two is
 * whether outbound work goes out today.
 *
 * `waiting` is what is waiting anywhere, added up from the counts the response
 * carries, and never the length of the list on screen. With the server
 * narrowing, that list is empty exactly when the picked campaign is empty, so
 * reading it here would answer "is there anything left" with "is there anything
 * left on this campaign" and end a shift with the rest of the queue untouched.
 */
export function readEmptyQueue(waiting: number, filters: ApprovalFilters): QueueEmptyReading {
  if (waiting > 0 && sieveActive(filters)) {
    return {
      title: NO_SIEVE_MATCH_TITLE,
      description: NO_SIEVE_MATCH_DESCRIPTION,
      line: NO_SIEVE_MATCH_LINE,
      clearable: true,
    }
  }
  if (waiting > 0 && anyApprovalFilterActive(filters)) {
    return {
      title: NO_MATCH_TITLE,
      description: NO_MATCH_DESCRIPTION,
      line: NO_MATCH_LINE,
      clearable: true,
    }
  }
  return {
    title: CAUGHT_UP_TITLE,
    description: CAUGHT_UP_DESCRIPTION,
    line: CAUGHT_UP_LINE,
    clearable: false,
  }
}

// ---------------------------------------------------------------------------
// Which draft is in front of somebody
// ---------------------------------------------------------------------------

export const ELSEWHERE_DRAFT_TITLE = 'That draft is not on this sequence'
export const ELSEWHERE_DRAFT_DESCRIPTION =
  'It is either waiting on another sequence or already decided. Show every sequence and it comes back if it is still waiting.'

export const SIEVED_DRAFT_TITLE = 'That draft is not in this view'
export const SIEVED_DRAFT_DESCRIPTION =
  'It may be in another state, may not match the search, or may have been decided already. Clear the filters and it comes back if it is still waiting.'

export const GONE_DRAFT_TITLE = 'That draft is no longer in the queue'
export const GONE_DRAFT_DESCRIPTION =
  'Somebody may have decided it already, or the person it was written for replied and the step came off the queue. Nothing here has been decided on your behalf.'
export const FIRST_DRAFT_LABEL = 'Go to the first draft'

export type SelectionReading =
  /** This draft is on screen and is the one the address bar names. */
  | { kind: 'draft'; id: string; index: number }
  /** Nothing is asked for and there is nothing to land on. */
  | { kind: 'empty' }
  /**
   * A campaign is picked and the draft asked for is not on it. Whether it is
   * waiting on another campaign or has left the queue is not something a
   * narrowed page can answer, so the reading does not claim either.
   */
  | { kind: 'elsewhere'; id: string }
  /** The whole queue is on screen and the draft asked for is not in it. */
  | { kind: 'gone'; id: string }

/**
 * The draft to show, given what the address bar asks for.
 *
 * The one rule worth stating out loud: a requested draft that is not in the
 * visible list is never quietly replaced by a different one. It used to be, and
 * that is the shape of the accident this screen can have. A reviewer opens a
 * draft, something changes the list underneath them, the pane fills with
 * somebody else's message, and the `a` they were already pressing approves it.
 * Being told the draft moved costs a click. The other way costs a send.
 *
 * Landing on the first draft when nothing is asked for is not that substitution
 * and stays: nothing was open, so nothing is being taken away. That is also
 * what makes picking a campaign land on that campaign's first draft, since
 * picking one clears the open draft in the same write. The reviewer asked to
 * look at that campaign and the screen answers by showing it.
 *
 * `visible` is now the server's answer for the campaign in `filters` and can be
 * empty because the answer for a freshly picked campaign has not arrived yet.
 * That moment reads exactly like a settled empty list: nothing asked for gives
 * `empty`, something asked for gives a stated reason, and neither is a draft.
 * So there is nothing for a keystroke to land on while a request is in flight,
 * which is the property that has to survive the list arriving already narrowed.
 * The page covers the same moment with its skeleton, and this holds whether it
 * does or not.
 *
 * The two ways of being missing are told apart by what is actually known rather
 * than by which is likelier. With no campaign picked, the queue is on screen and
 * a draft that is not in it has left it. With one picked, the page holds a
 * single campaign and a draft that is not on it could be waiting on another or
 * could have been decided a moment ago, and nothing on screen can say which.
 */
export function readSelection(
  visible: readonly ApprovalItem[],
  requestedId: string | null,
  filters: ApprovalFilters,
): SelectionReading {
  if (requestedId === null || requestedId.trim() === '') {
    const first = visible[0]
    return first === undefined ? { kind: 'empty' } : { kind: 'draft', id: first.id, index: 0 }
  }
  const id = requestedId.trim()
  const index = visible.findIndex((item) => item.id === id)
  if (index >= 0) return { kind: 'draft', id, index }
  return anyApprovalFilterActive(filters) ? { kind: 'elsewhere', id } : { kind: 'gone', id }
}

export interface MissingDraftReading {
  title: string
  description: string
  /** The label for the one button out of here, or null when there is nowhere to go. */
  action: string | null
  /** Whether that button clears the filter rather than moving in the list. */
  clearsFilter: boolean
}

/**
 * What to say when the draft somebody asked for is not the draft they can see.
 *
 * Clearing the filter is the way out of `elsewhere` whichever of its two
 * meanings is the true one. If the draft is waiting on another campaign it
 * comes back into the list, and if it was decided the whole queue is the place
 * that says so.
 */
export function readMissingDraft(
  reading: SelectionReading,
  visibleCount: number,
  /** What narrows the list, so a search or a status is named rather than a campaign. */
  filters?: ApprovalFilters,
): MissingDraftReading | null {
  if (reading.kind === 'elsewhere' && filters !== undefined && sieveActive(filters)) {
    return {
      title: SIEVED_DRAFT_TITLE,
      description: SIEVED_DRAFT_DESCRIPTION,
      action: CLEAR_ALL_FILTERS_LABEL,
      clearsFilter: true,
    }
  }
  if (reading.kind === 'elsewhere') {
    return {
      title: ELSEWHERE_DRAFT_TITLE,
      description: ELSEWHERE_DRAFT_DESCRIPTION,
      action: CLEAR_FILTER_LABEL,
      clearsFilter: true,
    }
  }
  if (reading.kind === 'gone') {
    return {
      title: GONE_DRAFT_TITLE,
      description: GONE_DRAFT_DESCRIPTION,
      action: visibleCount > 0 ? FIRST_DRAFT_LABEL : null,
      clearsFilter: false,
    }
  }
  return null
}

/**
 * Where `j` and `k` land, over the drafts actually on screen.
 *
 * Only from a draft. With nothing selected there is no place to move from, and
 * moving would be the keyboard choosing a draft nobody looked at.
 */
export function moveSelection(
  visible: readonly ApprovalItem[],
  reading: SelectionReading,
  delta: number,
): string | null {
  if (visible.length === 0) return null
  if (reading.kind !== 'draft') return null
  const target = visible[Math.min(visible.length - 1, Math.max(0, reading.index + delta))]
  return target?.id ?? null
}

/**
 * Where to land once a draft has been decided.
 *
 * Worked out from the decided draft rather than from the selected one, because
 * a decision resolves after the click and the list can have moved on by then,
 * either because a refetch landed or because the campaign was changed while the
 * request was in flight. A draft that is no longer on screen gives up its place
 * in the queue and returns null, which puts the screen back on the first
 * visible draft rather than on a neighbour from a list nobody is looking at.
 */
export function afterDecision(
  visible: readonly ApprovalItem[],
  decidedId: string,
): string | null {
  const index = visible.findIndex((item) => item.id === decidedId)
  if (index < 0) return null
  const next = visible[index + 1] ?? visible[index - 1] ?? null
  return next?.id ?? null
}

// ---------------------------------------------------------------------------
// One box per message
// ---------------------------------------------------------------------------

/**
 * A device DM goes out as a short burst of messages typed in one visit to the
 * thread, so the editor holds a list of messages rather than one block of text.
 *
 * The whole burst is still one action and one stored body. `generated_content`
 * keeps the single record of what was said and the line breaks inside it are
 * the boundaries between messages, which is why everything below routes through
 * `splitIntoMessages` and `joinMessages` from the shared kernel instead of
 * splitting on a newline at its own site. The approval route asserts that
 * splitting the joined text gives back exactly the messages it was handed and
 * refuses the approval when it does not. A second splitter here that trimmed
 * differently would produce a refusal nobody at the review desk could explain.
 *
 * WHY EMAIL IS DELIBERATELY NOT PART OF THIS
 *
 * `channelSendsBursts` is false for email, and that is load bearing rather than
 * tidy. An email body is meant to have paragraphs in it, and every blank line in
 * one is a blank line the writer put there. Treating those as message
 * boundaries would quietly rewrite every email draft in the queue into a list of
 * fragments. So a draft that does not send bursts is held as a single entry
 * carrying the whole body, newlines and all, and the editor renders it in the
 * same one textarea it always has.
 *
 * WHY AN EMPTY BOX IS ALLOWED TO EXIST AND NOT TO BE APPROVED
 *
 * Pressing Enter at the end of a message opens an empty box below it, the way a
 * chat composer does, and that box has to survive long enough to be typed into.
 * But an empty message is not something a phone can send, and dropping it
 * silently at approval would mean the person sent fewer messages than the screen
 * showed them. So an empty box blocks the approval and says which box it is.
 */

/**
 * How many characters a limit counts this as.
 *
 * DELIBERATELY NOT THE NUMBER A PERSON WOULD COUNT. `"🚀"` is one character to
 * read and two to every limit in this product, because the server measures with
 * `text.length` and JavaScript strings are UTF-16. The counter's job is not to
 * describe the text, it is to predict the refusal, and a counter that disagrees
 * with the thing that refuses is worse than no counter: it says 200 of 200 in
 * neutral grey, the Approve button stays lit, and the sentence that comes back
 * is about a limit the screen had just said was met.
 *
 * Measured: three rocket emoji and 197 letters is 200 code points and 203 UTF-16
 * units, so an email subject built that way passed here and was refused by
 * `subject_too_long` at packages/personalize/src/validate/validators.ts.
 *
 * One function for the body and the subject, so there is one counting rule on
 * this screen rather than two that agree until somebody edits one of them.
 * `approvals-model.test.ts` runs it against the real validator, which is the
 * only thing that keeps this honest.
 */
export function countAgainstLimit(text: string): number {
  return text.length
}

/** What the editor is allowed to do to a draft's messages. */
export interface MessageRules {
  /** True when this channel and action type the messages into a thread one at a time. */
  bursts: boolean
  /** The most messages one send can carry. Always 1 when `bursts` is false. */
  ceiling: number
  /** The platform's character budget over the whole body, or null when it has none. */
  maxLength: number | null
}

/**
 * The rules for one draft.
 *
 * The ceiling is a safety setting and is never written down here. When the
 * response does not carry one, which happens only to a tab that was open before
 * the field existed, the ceiling becomes the number of messages the draft
 * already has. That widens nothing: the boxes on screen can be rewritten,
 * reordered and removed, and the one thing that cannot happen is a box being
 * added past a limit nobody told this screen about.
 */
export function readMessageRules(item: ApprovalItem): MessageRules {
  const bursts = channelSendsBursts(item.channel, item.action)
  const maxLength = item.maxLength ?? null
  if (!bursts) return { bursts: false, ceiling: 1, maxLength }
  const stated = item.maxMessages
  const ceiling =
    stated === undefined ? Math.max(1, splitIntoMessages(item.body).length) : Math.max(1, stated)
  return { bursts: true, ceiling, maxLength }
}

/**
 * The boxes a draft opens with.
 *
 * Always at least one box, including for a draft that generation parked with an
 * empty body, because that draft is waiting for somebody to write it and an
 * editor with no box in it is an editor you cannot type into.
 */
export function openMessages(item: ApprovalItem): string[] {
  if (!channelSendsBursts(item.channel, item.action)) return [item.body]
  const parts = splitIntoMessages(item.body)
  return parts.length === 0 ? [''] : parts
}

/**
 * The messages that would actually go out, in order.
 *
 * Deliberately expressed as a round trip through the shared splitter rather than
 * as a trim and a filter written here. That is exactly the check the approval
 * route runs against what it is sent, so anything this returns is something the
 * route will accept, and a change to what a boundary means cannot leave the two
 * sides disagreeing.
 */
export function sendableMessages(parts: readonly string[], rules: MessageRules): string[] {
  if (!rules.bursts) return [parts[0] ?? '']
  return splitIntoMessages(joinMessages(parts))
}

/** The text that would be stored as the record of what was said. */
export function joinedBody(parts: readonly string[], rules: MessageRules): string {
  if (!rules.bursts) return parts[0] ?? ''
  return joinMessages(sendableMessages(parts, rules))
}

/** True when somebody has changed a message, added one, removed one or moved one. */
export function messagesEdited(parts: readonly string[], item: ApprovalItem): boolean {
  const opened = openMessages(item)
  if (parts.length !== opened.length) return true
  return parts.some((part, index) => part !== opened[index])
}

// ---------------------------------------------------------------------------
// What the editor says while somebody is in it
// ---------------------------------------------------------------------------

export const ADD_MESSAGE_LABEL = 'Add a message'
export const REMOVE_MESSAGE_LABEL = 'Remove this message'
export const MOVE_MESSAGE_UP_LABEL = 'Move this message up'
export const MOVE_MESSAGE_DOWN_LABEL = 'Move this message down'
export const ONLY_MESSAGE_REASON = 'A draft has to say something, so the last message stays.'
export const NOTHING_TO_SEND_NOTE =
  'There is nothing to send. Write something, or reject this draft.'

/**
 * What several messages actually do, said once above the boxes.
 *
 * No number for the pause between them, on purpose. That gap is a safety
 * setting resolved per brand, so any figure printed here would be a guess that
 * looks like a fact.
 */
export const BURST_NOTE =
  'These go out one after another in a single visit to the thread, with a short pause between them, the way a person types.'

/**
 * How the two gestures that shape a burst work, said once under the boxes.
 *
 * Neither is discoverable. Enter in a chat composer sends, so nobody expects it
 * to open a box here, and nobody tries pasting a whole burst into one field to
 * see what happens. One line under the editor is what turns both into things
 * people use rather than things they find out about by accident.
 */
export const MESSAGE_KEYS_NOTE =
  'Enter starts the next message. Pasted text with line breaks in it splits into messages on its own.'

/** Said under the counter, and only when there is more than one box to count. */
export const JOINED_BUDGET_NOTE =
  'The platform counts every message together, so that budget covers all of them.'

/** One box, named by where it sits. */
export function messageBoxLabel(index: number): string {
  return `Message ${index + 1}`
}

/** The heading over the editor, which is about how many things are going out. */
export function copyCardTitle(count: number): string {
  return count === 1 ? 'The message' : 'The messages'
}

/** Why the add button is off. Shown beside it, because a disabled button shows no hover. */
export function addMessageBlockedReason(ceiling: number): string {
  return `One send carries ${plural(ceiling, 'message')} at most, so there is no room for another.`
}

/**
 * Why a paste was refused.
 *
 * It names both numbers, because the useful next move is to work out how much
 * has to come out, and a bare "too many" leaves somebody counting line breaks.
 */
export function pasteRefusalNote(wanted: number, ceiling: number): string {
  return `That paste would make ${plural(wanted, 'message')} and one send carries ${plural(ceiling, 'message')}. Paste less of it, or put the rest into a message that is already here.`
}

/**
 * An empty box, said beside the button it is stopping.
 *
 * It names the box, because the button and the box are not next to each other
 * and a burst can be several boxes long.
 */
export function emptyMessageNote(index: number): string {
  return `${messageBoxLabel(index)} has nothing in it. Write something in it, or remove it.`
}

/**
 * The same fact, said under the box itself.
 *
 * Shorter on purpose. The box is right there, so repeating which one it is and
 * what to do about it would print the sentence beside the button twice.
 */
export const EMPTY_MESSAGE_FIELD_NOTE = 'Nothing in this one yet.'

/**
 * More messages than a send can carry.
 *
 * Reachable without anybody pasting anything: the ceiling is a safety setting,
 * so lowering it puts every draft already holding more messages than that into
 * this state. Saying how many have to go is the only useful sentence here.
 */
export function overCeilingNote(count: number, ceiling: number): string {
  const excess = count - ceiling
  return `This draft has ${plural(count, 'message')} and one send carries ${plural(ceiling, 'message')}. Remove ${plural(excess, 'message')} before approving.`
}

/** Over the platform's character budget, measured over everything that would go out. */
export function overLimitNote(over: number): string {
  return `This is ${plural(over, 'character')} over what the platform accepts, so it cannot be approved until it fits.`
}

// ---------------------------------------------------------------------------
// Editing the list
// ---------------------------------------------------------------------------

/** The result of one gesture in the editor. */
export interface MessageEdit {
  /** The list after the gesture, or the list unchanged when it was refused. */
  messages: string[]
  /** Where the caret should land, or null to leave focus where it is. */
  focus: { index: number; caret: number } | null
  /** The sentence to show when the gesture was refused, or null when it was not. */
  refusal: string | null
}

function unchanged(parts: readonly string[]): MessageEdit {
  return { messages: [...parts], focus: null, refusal: null }
}

function endOf(parts: readonly string[], index: number): { index: number; caret: number } {
  return { index, caret: (parts[index] ?? '').length }
}

/**
 * Enter inside a box, which opens a new box below it.
 *
 * A chat composer sends on Enter. Here the send is behind an Approve button, so
 * the same keystroke means the same thing it means to the writer, which is
 * "that is the end of that message". Whatever sat to the right of the caret goes
 * into the new box, so pressing it in the middle of a message splits it there
 * rather than dropping half of it.
 *
 * Both halves are allowed to be empty. The box has to exist before it can be
 * typed into, and `readMessages` is what stops an empty one being approved.
 */
export function breakMessage(
  parts: readonly string[],
  index: number,
  caret: number,
  rules: MessageRules,
): MessageEdit {
  const current = parts[index]
  if (current === undefined) return unchanged(parts)
  // Nothing happens at the ceiling, and it says why. A keystroke that quietly
  // does nothing reads as a broken box rather than as a limit.
  if (parts.length >= rules.ceiling) {
    return { messages: [...parts], focus: null, refusal: addMessageBlockedReason(rules.ceiling) }
  }
  const at = Math.max(0, Math.min(caret, current.length))
  const messages = [...parts]
  messages.splice(index, 1, current.slice(0, at), current.slice(at))
  return { messages, focus: { index: index + 1, caret: 0 }, refusal: null }
}

/**
 * Pasting text with line breaks in it, which is the gesture this editor exists
 * for. Somebody writes the burst somewhere else and drops it in whole.
 *
 * The box is rebuilt as what was to the left of the selection, plus the pasted
 * text, plus what was to the right, and that whole string is split the one way
 * anything is ever split here. So a paste in the middle of a message keeps both
 * of its halves attached to the right neighbours instead of stranding the tail.
 *
 * Over the ceiling it refuses the whole paste rather than keeping the first few
 * messages. Keeping a prefix would drop the rest of somebody's text with nothing
 * on screen saying which part went missing, and the thing that got dropped is
 * the end of a message somebody wrote to a real person.
 */
export function pasteIntoMessage(
  parts: readonly string[],
  index: number,
  selection: { start: number; end: number },
  pasted: string,
  rules: MessageRules,
): MessageEdit {
  const current = parts[index]
  if (current === undefined) return unchanged(parts)
  const start = Math.max(0, Math.min(selection.start, current.length))
  const end = Math.max(start, Math.min(selection.end, current.length))
  const head = current.slice(0, start)
  const tail = current.slice(end)

  /*
    A channel that does not send bursts has no boundaries in its body to find.
    Every line break in an email is one the writer put there, so splitting here
    would take a message somebody wrote and shred it into fragments, and it
    would do it on an ordinary keystroke. The text goes in exactly as it came.

    This is the guard rather than the call site because both of the ways a value
    reaches a box come through this function, and only one of them is a paste.
  */
  if (!rules.bursts) {
    const messages = [...parts]
    messages[index] = head + pasted + tail
    return { messages, focus: { index, caret: (head + pasted).length }, refusal: null }
  }

  const pieces = splitIntoMessages(head + pasted + tail)
  // Everything in the box, and everything pasted into it, was whitespace. One
  // empty box left behind is the honest result, and it is still a box.
  const replacement = pieces.length === 0 ? [''] : pieces

  const wanted = parts.length - 1 + replacement.length
  if (wanted > rules.ceiling) {
    return { messages: [...parts], focus: null, refusal: pasteRefusalNote(wanted, rules.ceiling) }
  }

  const messages = [...parts]
  messages.splice(index, 1, ...replacement)

  // Where the caret ends up: at the end of the last message the pasted text
  // contributed to, so carrying on typing carries on where the paste stopped.
  // When the paste ends on a line break, the next thing typed belongs to the
  // box after it, so the caret goes to the front of that one instead.
  const throughPaste = splitIntoMessages(head + pasted)
  const endsOnBoundary = /[\r\n]$/.test(pasted)
  const offset = endsOnBoundary ? throughPaste.length : Math.max(0, throughPaste.length - 1)
  const wantedIndex = index + offset
  const lastNew = index + replacement.length - 1
  const focusIndex = Math.min(wantedIndex, lastNew)
  // Asking for a box past the end happens when the paste ended on a line break
  // and there was nothing after it to fill the box that break asked for. The
  // caret then belongs at the end of the last box there is, not at the front of
  // it, which is where a clamped index alone would have left it.
  const caret =
    focusIndex !== wantedIndex
      ? (messages[focusIndex] ?? '').length
      : endsOnBoundary
        ? 0
        : (throughPaste[throughPaste.length - 1] ?? '').length
  return { messages, focus: { index: focusIndex, caret }, refusal: null }
}

/**
 * A box's whole value changing, which is ordinary typing and also the backstop.
 *
 * Typing cannot produce a line break in a box, because Enter is handled and
 * paste is handled. Dropping text onto a box can, and so can a browser filling
 * one in. A line break that survived inside a box would be a message boundary
 * the person could not see, so anything arriving with one is split the same way
 * a paste is, and refused the same way when that would not fit.
 */
export function setMessage(
  parts: readonly string[],
  index: number,
  value: string,
  rules: MessageRules,
): MessageEdit {
  const current = parts[index]
  if (current === undefined) return unchanged(parts)
  if (!rules.bursts || !/[\r\n]/.test(value)) {
    const messages = [...parts]
    messages[index] = value
    return { messages, focus: null, refusal: null }
  }
  return pasteIntoMessage(parts, index, { start: 0, end: current.length }, value, rules)
}

/** A new empty box on the end, which is what the add button does. */
export function addMessage(parts: readonly string[], rules: MessageRules): MessageEdit {
  if (parts.length >= rules.ceiling) {
    return { messages: [...parts], focus: null, refusal: addMessageBlockedReason(rules.ceiling) }
  }
  const messages = [...parts, '']
  return { messages, focus: { index: messages.length - 1, caret: 0 }, refusal: null }
}

/**
 * Removing a box. Never the last one, because a draft with no boxes cannot be
 * typed into and the way to send nothing is to reject the draft.
 *
 * The caret goes to the end of the box above, and for the first box to the end
 * of the one that has slid up into its place, so the keyboard is left somewhere
 * a person can carry on typing from.
 */
export function removeMessage(parts: readonly string[], index: number): MessageEdit {
  if (index < 0 || index >= parts.length) return unchanged(parts)
  if (parts.length <= 1) return { messages: [...parts], focus: null, refusal: ONLY_MESSAGE_REASON }
  const messages = [...parts]
  messages.splice(index, 1)
  const landing = Math.max(0, index - 1)
  return { messages, focus: endOf(messages, landing), refusal: null }
}

/**
 * Moving a message up or down.
 *
 * Buttons rather than a drag target. This queue is worked from the keyboard, one
 * draft at a time, and a drag handle is the one control a keyboard cannot reach.
 * Focus follows the message rather than the position, so pressing move twice
 * moves the same message twice.
 */
export function moveMessage(parts: readonly string[], index: number, delta: number): MessageEdit {
  const target = index + delta
  if (index < 0 || index >= parts.length) return unchanged(parts)
  if (target < 0 || target >= parts.length) return unchanged(parts)
  const messages = [...parts]
  const moved = messages[index] as string
  messages[index] = messages[target] as string
  messages[target] = moved
  return { messages, focus: endOf(messages, target), refusal: null }
}

// ---------------------------------------------------------------------------
// Whether the list can be approved
// ---------------------------------------------------------------------------

export interface MessagesReading {
  /** What would go out, in order. Empty when there is nothing to send at all. */
  sendable: string[]
  /** The text that would be stored, which is what the platform's limit measures. */
  joined: string
  /** Characters in that text, counted the way the editor counts them. */
  length: number
  /** True when every box is blank. */
  empty: boolean
  overLimit: boolean
  /** True when the limit is close enough that the counter should say so. */
  nearLimit: boolean
  /** More messages than one send can carry, which a lowered ceiling can produce. */
  overCeiling: boolean
  /** The boxes holding nothing, so each one can say so where it is. */
  emptyIndexes: number[]
  /** The one sentence saying why this cannot be approved, or null when it can. */
  blockedReason: string | null
}

/**
 * Everything the screen needs to know about the list in front of somebody.
 *
 * The character budget is measured over the joined text and never per box,
 * because the platform limits the message it receives and a burst is stored and
 * counted as one body. A per-box count is still shown, because it is what tells
 * somebody which box to shorten, but it is never the thing that refuses.
 *
 * The order of the refusals is the order somebody can act on them. Nothing at
 * all comes first, then too many messages, then a blank box among several, then
 * length. Reporting length first on a draft with an empty box would send them to
 * shorten a message when the fix is two rows further down.
 */
export function readMessages(parts: readonly string[], rules: MessageRules): MessagesReading {
  const sendable = sendableMessages(parts, rules)
  const joined = joinedBody(parts, rules)
  const length = countAgainstLimit(joined)
  const limit = rules.maxLength
  const overLimit = limit !== null && length > limit
  const nearLimit = limit !== null && !overLimit && length > limit * 0.9
  const empty = joined.trim().length === 0
  const overCeiling = parts.length > rules.ceiling
  const emptyIndexes = parts.reduce<number[]>((found, part, index) => {
    if (part.trim().length === 0) found.push(index)
    return found
  }, [])

  const firstEmpty = emptyIndexes[0]
  const blockedReason = empty
    ? NOTHING_TO_SEND_NOTE
    : overCeiling
      ? overCeilingNote(parts.length, rules.ceiling)
      : firstEmpty !== undefined
        ? emptyMessageNote(firstEmpty)
        : overLimit
          ? overLimitNote(length - (limit ?? 0))
          : null

  return {
    sendable,
    joined,
    length,
    empty,
    overLimit,
    nearLimit,
    overCeiling,
    emptyIndexes,
    blockedReason,
  }
}

// ---------------------------------------------------------------------------
// The subject line
// ---------------------------------------------------------------------------

/**
 * WHY THE SUBJECT IS ITS OWN SMALL WORLD DOWN HERE and not a field on
 * `MessageRules`.
 *
 * The two are measured against different things and refused for different
 * reasons. A body's budget is the platform's, counted over every message joined
 * together, and the ceiling on how many messages there are is a safety setting.
 * A subject has one box, always, a fixed 200 characters on the two channels that
 * have one, and its own refusal for being empty. Folding it into the message
 * rules would put a count that means "characters over all the messages" next to
 * one that means "characters in the one line", and the first thing anybody would
 * do with that pair is add them up.
 *
 * The screen also has to be able to draw nothing at all here, which no reading
 * of the messages ever does.
 */

/** What the editor may do to a draft's subject line. */
export interface SubjectRules {
  /** The channel's ceiling, or null when it does not cap the line. */
  maxLength: number | null
  /** True when the channel refuses a draft whose subject is blank. */
  required: boolean
}

/**
 * The subject rules for one draft, or null when there is no subject to edit.
 *
 * Null in two different situations, and it is worth being clear that they are
 * both "do not offer a subject field":
 *
 *   - The channel has no subject line. Every channel but email and InMail.
 *   - The response predates this field, which is a tab that was already open
 *     when it shipped. That tab goes on showing whatever subject it was sent and
 *     goes on approving drafts, and the one thing it does not do is offer an
 *     edit whose rules nobody has told it. Inventing "200 characters, required"
 *     here would be this screen enforcing a limit the server never named.
 */
export function readSubjectRules(item: ApprovalItem): SubjectRules | null {
  const rules = item.subjectRules
  if (rules === null || rules === undefined) return null
  return { maxLength: rules.maxLength ?? null, required: rules.required === true }
}

/** The subject the editor opens with. Empty string when the draft has none. */
export function openSubject(item: ApprovalItem): string {
  return item.subject ?? ''
}

/** True when somebody has changed the subject line. */
export function subjectEdited(subject: string, item: ApprovalItem): boolean {
  return subject.trim() !== openSubject(item).trim()
}

export const SUBJECT_LABEL = 'Subject'
export const SUBJECT_PLACEHOLDER = 'The line they see before they open it'

export const SUBJECT_MISSING_NOTE =
  'This one needs a subject line, so write one before approving.'

/**
 * What the empty field itself says, which is not the sentence beside the button.
 *
 * The same split the message boxes have. Beside the Approve button is the reason
 * the draft cannot go, worded for somebody deciding what to do. In the field is
 * a note about that field, worded for somebody looking straight at it. Printing
 * the long sentence in both places puts one string on screen twice, six lines
 * apart, and the second copy teaches people that the notes under fields are
 * repeats worth skipping.
 */
export const EMPTY_SUBJECT_FIELD_NOTE = 'Nothing written yet.'

/** Over the channel's ceiling for the line, which is a different budget from the body's. */
export function subjectOverLimitNote(over: number): string {
  return `The subject is ${plural(over, 'character')} over what this channel accepts, so it has to be shorter before this can be approved.`
}

/** Everything the screen needs to know about the subject in front of somebody. */
export interface SubjectReading {
  /** Characters in the line, counted the way the editor counts them. */
  length: number
  overLimit: boolean
  /** Close enough to the ceiling that the counter should say so. */
  nearLimit: boolean
  /** Required and blank. */
  missing: boolean
  /** The one sentence saying why this cannot be approved, or null when it can. */
  blockedReason: string | null
}

/**
 * How the subject reads right now.
 *
 * A draft with no subject rules reads as fine, always, with a length of zero.
 * That is not a shortcut: there is no subject on screen to be wrong, and a
 * reading that could refuse a draft on a channel with no subject line would
 * block every LinkedIn message in the queue on a field nobody can see.
 *
 * Missing before over-limit, in the order somebody can act on them. There is
 * only one way to be both, which is a required line holding nothing but spaces
 * past the ceiling, and even then "write one" is the sentence that helps.
 */
export function readSubject(subject: string, rules: SubjectRules | null): SubjectReading {
  if (rules === null) {
    return { length: 0, overLimit: false, nearLimit: false, missing: false, blockedReason: null }
  }
  const length = countAgainstLimit(subject)
  const limit = rules.maxLength
  const overLimit = limit !== null && length > limit
  const nearLimit = limit !== null && !overLimit && length > limit * 0.9
  const missing = rules.required && subject.trim().length === 0
  const blockedReason = missing
    ? SUBJECT_MISSING_NOTE
    : overLimit
      ? subjectOverLimitNote(length - (limit ?? 0))
      : null
  return { length, overLimit, nearLimit, missing, blockedReason }
}

/**
 * The server's own objection about the subject, put back beside the box.
 *
 * The same job `messageIssueNotes` does for the boxes below, and it has to be a
 * second function rather than a branch of that one: an issue about a message
 * names which message with `part`, and an issue about the subject names no part
 * at all, so the two are found by different keys. `path` is what the approve
 * route sets from the validator's `field`, and the values it can hold are
 * `body` and `subject`.
 *
 * Read defensively, and anything that is not a subject objection with something
 * to say is left out, so it goes on showing in the error panel above rather than
 * being attached to the wrong field.
 */
export function subjectIssueNote(details: Record<string, unknown> | undefined): string | null {
  const issues = details?.issues
  if (!Array.isArray(issues)) return null
  for (const issue of issues) {
    if (typeof issue !== 'object' || issue === null) continue
    const record = issue as Record<string, unknown>
    if (record.path !== 'subject') continue
    const message = record.message
    if (typeof message !== 'string' || message.trim() === '') continue
    // First one wins, so a line carrying two objections shows the one the server
    // put first rather than whichever happened to be read last.
    return message
  }
  return null
}

/**
 * What the subject field says about itself.
 *
 * What is on screen wins over what the server said, for the same reason a blank
 * box does below: the server's objection is about the last approval it refused,
 * and the field in front of somebody is about now. They can only disagree when
 * the line has been changed since that refusal, and then the line is the truth.
 *
 * Nothing at all for a line that is merely too long. The counter above it is
 * already red and counting, and the sentence saying how far over sits beside the
 * Approve button, which is the control it turns off. A third statement of the
 * same fact, between the two, is noise.
 */
export function subjectNote(
  reading: SubjectReading,
  details: Record<string, unknown> | undefined,
): string | null {
  if (reading.missing) return EMPTY_SUBJECT_FIELD_NOTE
  if (reading.overLimit) return null
  return subjectIssueNote(details)
}

/**
 * What the approve request carries.
 *
 * Three answers, and the reason for each:
 *
 *   - More than one message goes as `messages`, edited or not. The server joins
 *     them and checks that splitting the join gives back what it was handed, so
 *     sending the list is what proves the boundaries the reviewer saw are the
 *     boundaries that get stored. It also normalises a body that arrived with a
 *     blank line in it into one that round trips.
 *   - One message that somebody changed goes as `body`, which is the request
 *     this screen has always sent and the one the diff and the audit row are
 *     built around.
 *   - One message nobody touched sends neither, which is how an approval with no
 *     edit is recorded, and that absence is itself a signal about the copy.
 *
 * The two keys are mutually exclusive on the wire, and this is the only place
 * that decides between them.
 */
export function approvePayload(
  parts: readonly string[],
  item: ApprovalItem,
  subject?: string,
): { body?: string; messages?: string[]; subject?: string } {
  const rules = readMessageRules(item)
  const sendable = sendableMessages(parts, rules)
  const copy =
    sendable.length > 1
      ? { messages: sendable }
      : messagesEdited(parts, item)
        ? { body: joinedBody(parts, rules) }
        : {}
  return { ...copy, ...subjectKey(subject, item) }
}

/**
 * The subject key, or nothing at all.
 *
 * A fourth answer alongside the three above and it follows the same rule they
 * do: an unchanged field is not sent, because the absence is what records an
 * approval nobody edited, and that absence is a signal about the copy.
 *
 * The key is independent of the two body keys, which is the whole point.
 * Somebody who rewrote only the subject sends only the subject, and the words
 * the model wrote go out recorded as the model's rather than being resubmitted
 * as an edit that changed nothing.
 *
 * Trimmed before comparing and before sending, because the server trims it too.
 * Without that, adding a trailing space to a subject would count as an edit
 * here, be sent, arrive trimmed and be stored as identical to what was already
 * there, and the row would carry an edit that changed nothing.
 *
 * `undefined` and a draft with no subject rules both send nothing, so no caller
 * has to remember which channels have a subject line.
 */
function subjectKey(subject: string | undefined, item: ApprovalItem): { subject?: string } {
  if (subject === undefined) return {}
  if (readSubjectRules(item) === null) return {}
  if (!subjectEdited(subject, item)) return {}
  return { subject: subject.trim() }
}

/**
 * The server's own objections, put back beside the box each one is about.
 *
 * A refused approval comes back carrying the issues that refused it, and an
 * issue about a burst names which message it is about. Reading that number here
 * is what turns "the approved text cannot be sent as written" into a sentence
 * under the box somebody has to fix.
 *
 * Everything is read defensively and anything that does not name a message it
 * can find is left out, so it goes on showing in the error panel above rather
 * than being attached to the wrong box or dropped. An older server that names no
 * message at all produces no per-box notes and loses nothing.
 */
export function messageIssueNotes(
  details: Record<string, unknown> | undefined,
  count: number,
): (string | null)[] {
  const notes: (string | null)[] = Array.from({ length: Math.max(0, count) }, () => null)
  const issues = details?.issues
  if (!Array.isArray(issues)) return notes
  for (const issue of issues) {
    if (typeof issue !== 'object' || issue === null) continue
    const record = issue as Record<string, unknown>
    const part = record.part
    const message = record.message
    if (typeof part !== 'number' || !Number.isInteger(part)) continue
    if (typeof message !== 'string' || message.trim() === '') continue
    const index = part - 1
    if (index < 0 || index >= notes.length) continue
    // First one wins, so a box carrying two objections shows the one the server
    // put first rather than whichever happened to be read last.
    if (notes[index] === null) notes[index] = message
  }
  return notes
}

/**
 * What each box says about itself, from both of the things that can object.
 *
 * A blank box wins over anything the server said, because the server's
 * objections are about the last approval it refused and a blank box is about
 * what is on screen now. They can only disagree when somebody has emptied a box
 * since that refusal, and in that case the box in front of them is the truth.
 */
export function messageNotes(
  reading: MessagesReading,
  count: number,
  details: Record<string, unknown> | undefined,
): (string | null)[] {
  const notes = messageIssueNotes(details, count)
  for (const index of reading.emptyIndexes) {
    if (index >= 0 && index < notes.length) notes[index] = EMPTY_MESSAGE_FIELD_NOTE
  }
  return notes
}

// ---------------------------------------------------------------------------
// The send that did not succeed
// ---------------------------------------------------------------------------

/**
 * A message that was approved, was handed over to be sent, and never arrived.
 *
 * WHY IT IS IN THIS QUEUE AND NOT ON A SCREEN OF ITS OWN. A device action leaves
 * the scheduler the moment it is handed to the phone, so when the phone reports a
 * fatal there is nothing routing it back: the allowance is returned, the sequence
 * carries on, and the step reads as done while the person is queued for a day-two
 * follow-up to an opener that never arrived. Measured twice on 2026-08-30, on two
 * real people. That is a message that needs a decision from somebody, and this
 * queue is already the one place a person looks at one message and decides what
 * happens to it. A second surface for the same job is the surface nobody opens,
 * so the failures come here instead.
 *
 * WHAT IS DIFFERENT ABOUT ONE OF THESE ROWS. Approve and Reject are the wrong
 * two questions. Both of them answer "should this go", and that was answered
 * already: these words went through this queue and somebody said yes. The one
 * question left is whether to send them again, so that is the one control the
 * pane offers, and it offers it only when the server says the command would
 * accept it.
 *
 * THE TONE. This is not an error and it is not the operator's mistake. The block
 * says what happened, hands over whatever came back word for word, and offers the
 * action that helps. It does not apologise and it does not explain the platform.
 */

/** The one word the list and the detail both use for this state. */
export const DELIVERY_BADGE_LABEL = 'Did not send'

/**
 * The badge's colour, named here rather than typed into the list.
 *
 * Deliberately one of the six the product already uses, and deliberately not
 * `danger`. Danger is what a suppressed person wears in this same badge row, and
 * a second red thing beside it would say the two are the same kind of problem.
 * They are not: one means never contact this person, the other means this message
 * still has to go. Warning is the tone the queue already uses for a draft that
 * needs a person before it can move, which is exactly what this is.
 */
export const DELIVERY_BADGE_TONE: Tone = 'warning'

export const DELIVERY_TITLE = 'This one did not send'

/**
 * The sentence under the title.
 *
 * It says the three facts somebody needs before they read anything else: it was
 * approved, it was handed over, and nothing went out. The last clause points at
 * the editor below, because the words in that editor are the words that were
 * meant to arrive and are the ones that will be sent if this goes again.
 */
export const DELIVERY_LEAD =
  'It was approved and handed over to be sent, and the send did not finish. Nothing went out, and the message below is the one that was meant to go.'

/**
 * The sentence for the case where part of it may already have arrived.
 *
 * MEASURED 2026-09-20 and it is why this is no longer one constant. A two-part DM
 * on X delivered part one, the app repainted its header in the gap, and the burst
 * stopped. The card said "Nothing went out" in this position while the reported
 * sentence directly beneath it said "It may have been delivered", and a Send again
 * button sat under both. Three claims on one card, two of them contradicting each
 * other, and the button repeats a message the person has already read.
 *
 * So this position now says the one thing that is true of every outcome in this
 * class: nobody knows, and the thread is the only place that does.
 */
export const DELIVERY_LEAD_MAY_HAVE_ARRIVED =
  'It was approved and handed over to be sent, and the send did not finish. Some of it may already have arrived, so sending again could repeat what they have read. Read the thread before deciding.'

/**
 * Which of the two belongs on this card.
 *
 * Keyed on the engine's own send vocabulary rather than on prose. `unconfirmed`
 * means the send button was tapped and the body could not be found afterwards,
 * and `burst_partly_delivered` means some parts of a multi-part message were
 * confirmed and the rest were not. Both mean the same thing to a reader: it may
 * be out there.
 *
 * Unknown text gets the cautious sentence, not the confident one. Claiming
 * nothing went out is the more dangerous mistake of the two, because it is the
 * one that invites a second send.
 */
const MAY_HAVE_ARRIVED = /unconfirmed|burst_partly_delivered|partly[_ ]delivered|may have been delivered/i

export function deliveryLead(reported: string | null | undefined): string {
  const said = (reported ?? '').trim()
  if (said === '') return DELIVERY_LEAD
  return MAY_HAVE_ARRIVED.test(said) ? DELIVERY_LEAD_MAY_HAVE_ARRIVED : DELIVERY_LEAD
}

export const DELIVERY_WORDS_HEADING = 'What came back'

/** What stands in for the reported sentence when nothing was reported at all. */
export const NO_DELIVERY_WORDS = 'Nothing came back to say what went wrong.'

export const RETRY_LABEL = 'Send again'

/**
 * The same button once somebody has changed the words.
 *
 * Approve already changes its own label for an edit, for the reason this one
 * does: the button is the last thing read before it is pressed, so it is the last
 * chance to say that the edit on screen is the thing about to be queued rather
 * than the copy that failed.
 */
export const RETRY_EDITED_LABEL = 'Send my edit again'

/**
 * What the button actually does, said under it.
 *
 * Not decoration. Pressing this puts the message back in the queue and the
 * scheduler picks it up on its next pass, and somebody who reads a button as
 * "sent" does not come back to check whether it was.
 */
export const RETRY_NOTE =
  'This puts it back in the queue. It goes out on the next pass, not the moment you press this.'

/**
 * What a refusal says when the server refused and did not say why.
 *
 * The server always means to send a sentence, and this is what happens when a new
 * refusal reason ships ahead of its words, or when the pane is being read by a
 * tab that was open before those words existed. A blank space where a button
 * would be reads as a bug, and a dead button with nothing beside it reads as one
 * too, so the pane says the one true thing it knows.
 */
export const RETRY_REFUSED_WITHOUT_REASON =
  'This one cannot go back in the queue right now. Open the campaign to see where this person has got to.'

export const RETRY_DONE_TITLE = 'Back in the queue'
export const RETRY_FAILED_TITLE = 'It did not go back in the queue'

/**
 * The failure on a row, or nothing at all.
 *
 * Read through a fallback rather than off the field, because a tab that was open
 * before this shipped is holding rows from before the key existed and is still
 * approving messages out of them. The answer that tab should get is "an ordinary
 * draft", which is exactly what every row in its hand was.
 */
export function readDelivery(item: ApprovalItem): ApprovalDelivery | null {
  return item.delivery ?? null
}

/**
 * Whatever came back, trimmed and otherwise untouched.
 *
 * Trimming is the whole of the tidying that happens to this string. It is not
 * capitalised, not shortened and not turned into a friendlier sentence, because
 * somebody deciding whether to send again is reading it for the detail rather
 * than for the tone. "timed out after 25s waiting for conversation_contact_name"
 * is ugly and it is also the only line on the screen that answers the question
 * they came with.
 */
export function failureSentence(reported: string | null | undefined): string {
  const said = (reported ?? '').trim()
  return said === '' ? NO_DELIVERY_WORDS : said
}

/**
 * How many goes this step has already had, when that is more than one.
 *
 * Silent on the first attempt, because "tried 1 time" under every failure says
 * nothing. A second or a third is the interesting case: it is the difference
 * between a phone that hiccupped once and a message that is never going to go.
 */
export function attemptNote(attempt: number): string | null {
  if (!Number.isFinite(attempt) || attempt <= 1) return null
  return `Tried ${formatNumber(attempt)} times`
}

/** What stands in for a name when the record has none. */
export const UNNAMED_PERSON = 'This person'

/**
 * The person, for a sentence that has to start with them.
 *
 * A blank name is rare and it is not impossible, and a confirmation that opens
 * with a space reads as the product losing track of who it just acted on, which
 * is the worst possible reading on a screen about a message that went astray.
 */
export function personName(fullName: string | null | undefined): string {
  const name = (fullName ?? '').trim()
  return name === '' ? UNNAMED_PERSON : name
}

/** The button that asks for the message to be written again, and what it does. */
export const REGENERATE_LABEL = 'Write it again'
export const REGENERATE_NOTE =
  'The model writes this one again. It comes back to this list in a minute or so, and nothing is sent until you approve it.'

/** What the toast says once the step has been handed back to the writer. */
export function regenerateDoneNote(name: string): string {
  return `${personName(name)} is being written again. It comes back to this list in a minute or so.`
}

/**
 * The question before words that exist are thrown away.
 *
 * A draft nobody could write has nothing to lose and is asked for straight. A
 * draft with words in it, or one somebody has edited here, is replaced by
 * whatever the model writes next, and that is worth a question.
 */
export function regenerateConfirm(hasWords: boolean): { title: string; description: string; confirmLabel: string } {
  return {
    title: 'Write this message again?',
    description: hasWords
      ? 'The words below are replaced by a new message the model writes for this person. Anything typed here goes with them. Nothing is sent until somebody approves the new one.'
      : 'The model writes this one again for this person. Nothing is sent until somebody approves it.',
    confirmLabel: REGENERATE_LABEL,
  }
}

/** What the toast says once a retry has been accepted. */
export function retryDoneNote(name: string): string {
  return `${personName(name)} is queued to be sent again. It goes out on the next pass.`
}

/** The button, or the sentence that stands where the button would have been. */
export type RetryControlReading =
  | { kind: 'offer'; label: string; note: string }
  | { kind: 'refused'; reason: string }

/**
 * Which of the two the pane draws.
 *
 * `allowed` is the only thing that decides it, and a refusal always carries
 * words. A reason arriving alongside `allowed` true is ignored rather than shown:
 * the command would accept that retry, so offering anything less than the button
 * would be the screen disagreeing with the server about what is possible.
 *
 * A refused retry is never a greyed-out button either. A dead control with no
 * explanation is the screen knowing why and not saying, and it leaves somebody
 * clicking something that does nothing.
 */
export function retryControl(
  retry: ApprovalDelivery['retry'],
  edited: boolean,
): RetryControlReading {
  if (retry.allowed) {
    return { kind: 'offer', label: edited ? RETRY_EDITED_LABEL : RETRY_LABEL, note: RETRY_NOTE }
  }
  const said = (retry.reason ?? '').trim()
  return { kind: 'refused', reason: said === '' ? RETRY_REFUSED_WITHOUT_REASON : said }
}

/**
 * What the retry request carries.
 *
 * A failed message is exactly when somebody wants to change the words, and the
 * editor is already on this screen, so the edit is saved before the step goes
 * back in the queue rather than being lost by pressing the only button on offer.
 *
 * An untouched draft sends nothing, and that absence is the whole answer: the
 * words already stored are the words that were approved, and rewriting them with
 * an identical copy would put an edit in the record that nobody made.
 *
 * The messages go as a list rather than as the joined text for the same reason
 * the approve request sends a list. The server joins them and checks that
 * splitting the join gives back exactly what it was handed, so sending the list
 * is what proves the boundaries the reviewer saw are the boundaries that get
 * stored.
 *
 * KNOWN EDGE, worth writing down rather than working around here. On a channel
 * that does not send bursts, which today means email, the list is one entry
 * holding the whole body with its paragraphs intact, and that body does not
 * survive a split and a join. The approve request has a second key for exactly
 * that case. This one does not, so an edited email body with a blank line in it
 * comes back refused, with the server's own sentence in the panel above the
 * button. Refused and said out loud is the right failure to have, and it is not
 * silent, but the fix is a `body` key on the retry request rather than anything
 * this function can do.
 */
export function retryPayload(
  parts: readonly string[],
  item: ApprovalItem,
  subject?: string,
): { messages?: string[]; subject?: string } {
  const copy = messagesEdited(parts, item)
    ? { messages: sendableMessages(parts, readMessageRules(item)) }
    : {}
  return { ...copy, ...subjectKey(subject, item) }
}

/**
 * The strip along the bottom of the pane, which has to describe the pane it is
 * under.
 *
 * A failed message has no Approve and no Reject, so a legend still offering `a`
 * and `r` would be teaching two keys that do nothing on the item in front of
 * somebody, on the one screen where a key that does nothing is indistinguishable
 * from a key that did something quietly.
 */
export function keyLegendItems(hasDelivery: boolean): { keys: string[]; label: string }[] {
  const moveAndEdit = [
    { keys: ['j', 'k'], label: 'move through the stack' },
    { keys: ['e'], label: 'edit the copy' },
  ]
  if (hasDelivery) {
    return [
      ...moveAndEdit,
      { keys: ['⌘', '↵'], label: 'send again' },
    ]
  }
  return [
    ...moveAndEdit,
    { keys: ['a'], label: 'approve' },
    { keys: ['r'], label: 'reject' },
    { keys: ['⌘', '↵'], label: 'approve while editing' },
  ]
}

/**
 * What a held step with no draft is, and what can be done about it, in one place.
 *
 * Pure so it is tested beside the rest of this page's reading. Two versions of
 * this line gave advice that released nobody. The first sent the reader to the
 * campaign "to see what it is waiting on", and the campaign page sent them back
 * here. The second said to give the step generated words, and editing a step's
 * words never wakes a person already held on it, so they stayed held.
 *
 * What is true now: every step the scheduler holds is written onto this list as a
 * draft, its literal or reused words included, so a held step with no draft is
 * one of two things. A step whose templates nobody has approved yet, which carries
 * on by itself once they are approved on the campaign. Or a step held before held
 * steps got a draft, or where the draft could not be written, which nothing in the
 * console can release and which somebody has to look at.
 */
export function heldWithoutDraftSentence(n: number): string {
  const which = n === 1 ? 'One step is' : `${n} steps are`
  return (
    `${which} held for an approval with nothing on this list to decide. If the step's templates are waiting to be ` +
    'approved, approve them on the campaign and it carries on by itself. Otherwise a held step should always have a ' +
    'draft here, so report it with the campaign named below.'
  )
}

/**
 * "N not started", said above the queue.
 *
 * WHY. A draft campaign's messages are approved here before it can launch, and
 * the ones not started yet are on nobody's list, so a queue that has been
 * worked through read as finished while a campaign still had messages to come.
 * The worker opens them by itself (the sweep on its tick), writes them, and
 * they land here. Null when there are none, and the page says nothing.
 */
export function notStartedLine(n: number, embedded: boolean): { title: string; sentence: string } | null {
  if (n <= 0) return null
  const where = embedded ? '' : ' in draft campaigns'
  return {
    title: `${n} not started`,
    sentence:
      `${n === 1 ? 'One message' : `${n} messages`}${where} ${n === 1 ? 'has' : 'have'} not been started yet, so ${n === 1 ? 'it is' : 'they are'} not on this list. ` +
      `${n === 1 ? 'It is' : 'They are'} opened by ${n === 1 ? 'itself' : 'themselves'} within a minute or two, written, and then ${n === 1 ? 'shows' : 'show'} up here for approval.`,
  }
}

/** How often the queue asks again while messages are still being written. */
export const QUEUE_REFRESH_MS = 15_000

/** How many identical readings before the number is called stuck rather than slow. */
export const STILL_READINGS = 3

export interface QueueWriting {
  /** The sentence above the list, or null when there is nothing to say. */
  sentence: string
  /** Warning is "somebody has to look at this". Info is "wait and it fills in". */
  tone: 'info' | 'warning'
  /** Milliseconds until the next ask, or false to stop asking. */
  refreshMs: number | false
}

/**
 * Whether the steps with no draft are being written right now, or stuck.
 *
 * WHY THIS IS NOT ONE SENTENCE ANY MORE. The screen used to say one thing about
 * a held step with no draft: that it should never happen, and to report it. That
 * is true of a step whose generation failed and completely wrong about a queue
 * being rewritten, which is a thing an operator does on purpose and which puts
 * eighty of these on the screen at once. Measured while rewriting the Blockcircle
 * Coinfest queue: 84 steps, each one perfectly healthy and about a minute from
 * having its message, and the screen calling every one of them a fault to report.
 *
 * THE TWO ARE TOLD APART BY MOVEMENT, not by a flag. There is no field that says
 * "a model is writing this one right now", and inventing one would mean a status
 * somebody has to remember to clear. A number that is going down is being worked
 * through. A number that has not moved across three asks is not, whatever anybody
 * meant it to be doing. That is evidence rather than a claim, which is the right
 * shape for a sentence that tells somebody to go and investigate.
 *
 * `history` is oldest first. Until there are enough readings to see movement the
 * sentence says the plain fact and nothing about why, because the honest answer
 * to "is this stuck" fifteen seconds in is that nobody knows yet.
 */
export function readQueueWriting(history: readonly number[]): QueueWriting | null {
  const now = history.at(-1) ?? 0
  if (now <= 0) return null

  const most = Math.max(...history)
  const written = most - now
  if (written > 0) {
    return {
      sentence:
        `${now === 1 ? 'One message is' : `${now} messages are`} still being written. ` +
        `${written === 1 ? 'One has' : `${written} have`} landed since this page opened, and the rest appear here as they are finished.`,
      tone: 'info',
      refreshMs: QUEUE_REFRESH_MS,
    }
  }

  const still = history.slice(-STILL_READINGS)
  if (still.length >= STILL_READINGS && still.every((reading) => reading === now)) {
    return { sentence: heldWithoutDraftSentence(now), tone: 'warning', refreshMs: QUEUE_REFRESH_MS }
  }

  return {
    sentence:
      `${now === 1 ? 'One step is' : `${now} steps are`} waiting for a message to be written. ` +
      'This page keeps looking, and they appear as they are finished.',
    tone: 'info',
    refreshMs: QUEUE_REFRESH_MS,
  }
}

// ---------------------------------------------------------------------------
// Approving a whole step at once
// ---------------------------------------------------------------------------

/**
 * One step of a campaign with drafts waiting, as the server counts it.
 *
 * WHY THIS EXISTS. The operator had 237 messages staged on one step of one
 * campaign and a desk that released them one press at a time. The server
 * approves each draft on its own, so what this screen owes the person is a clear
 * count before, a confirmation that names it, and after, a list of anybody whose
 * message could not be approved and why.
 */
export interface WaitingStep {
  stepIndex: number
  channel: Channel
  actionType: ActionType
  waiting: number
}

/** What the batch answered: who was approved, and who was not and why. */
export interface BatchApprovalResult {
  campaignId: Uuid
  stepIndex: number
  approved: { approvalId: Uuid; prospectId: Uuid; prospectName: string }[]
  failed: { approvalId: Uuid; prospectId: Uuid; prospectName: string; reason: string }[]
}

export const BATCH_SECTION_TITLE = 'Approve a whole step'

/**
 * Which campaign the whole-step control is about: the one picked, or, when
 * nothing is picked and only one campaign has anything waiting, that one. The
 * campaign picker is not drawn for a single campaign, and the queue that needed
 * this most was exactly that shape. Nothing when several are waiting and none is
 * picked, because a step number means nothing across campaigns.
 */
export function batchCampaignOf(filters: ApprovalFilters, campaigns: readonly CampaignCount[]): CampaignCount | { id: Uuid; name: null } | null {
  if (filters.campaignId !== '') return campaigns.find((row) => row.id === filters.campaignId) ?? { id: filters.campaignId, name: null }
  return campaigns.length === 1 ? (campaigns[0] ?? null) : null
}

/** The button for one step, with the number it will release. */
export function batchApproveLabel(step: WaitingStep): string {
  return `Approve all ${formatNumber(step.waiting)} on step ${step.stepIndex}`
}

export const BATCH_CONFIRM_TITLE = 'Approve every message on this step?'

/** The campaign behind the whole-step control, as the server sends it with the steps. */
export interface BatchCampaignState {
  status: string
  /** Empty unless the campaign is a draft whose sequence the launch would refuse. */
  launchIssues: { path: string; message: string }[]
}

/**
 * What the control says about the campaign before anything is approved, or null
 * when the campaign is running and approving sends.
 *
 * Review, 2026-09-14: Conor could approve all 237 drafts on step 19 of a draft
 * campaign whose launch is refused on that same step, and nothing on the desk
 * said that approval sends nothing until launch, or that the launch would not
 * come until the sequence was fixed.
 */
export function batchCampaignWarning(campaign: BatchCampaignState | null | undefined): string | null {
  if (campaign === null || campaign === undefined || campaign.status !== 'draft') return null
  const first = 'This campaign is a draft, so nothing approved here is sent until it launches.'
  const issue = campaign.launchIssues[0]
  if (issue === undefined) return first
  const why = issue.message.trim().replace(/[.!?]+$/, '')
  return `${first} It cannot launch yet: ${why}. Fix the sequence on the playbook first, or these messages wait.`
}

/**
 * What pressing yes does, said before it is done. The number is the one the
 * request carries, so if more arrive in the meantime the server refuses rather
 * than releasing messages nobody counted. A draft campaign's warning comes
 * first, because it changes what yes means.
 */
export function batchConfirmSentence(step: WaitingStep, campaignName: string | null, campaign: BatchCampaignState | null = null): string {
  const where = campaignName === null ? 'this campaign' : campaignName
  const count = step.waiting === 1 ? 'the one message' : `all ${formatNumber(step.waiting)} messages`
  const warning = batchCampaignWarning(campaign)
  return (
    (warning === null ? '' : `${warning} `) +
    `This approves ${count} waiting on step ${step.stepIndex} of ${where}, exactly as they are written. ` +
    'Each one is checked on its own before it is released, and any that fail the check stay here for you. ' +
    'If more have arrived since this count, nothing is approved and you are asked again.'
  )
}

/** One sentence for the result, then one line per person whose message stayed. */
export function batchResultSentence(result: BatchApprovalResult): string {
  const approved = result.approved.length
  const failed = result.failed.length
  const first = approved === 0 ? 'Nothing was approved.' : `Approved ${plural(approved, 'message')} on step ${result.stepIndex}.`
  if (failed === 0) return first
  return `${first} ${formatNumber(failed)} could not be approved and ${failed === 1 ? 'is' : 'are'} still waiting for you.`
}

export function batchFailureLine(row: BatchApprovalResult['failed'][number]): string {
  return `${row.prospectName}: ${row.reason}`
}

/**
 * What the queue says about a draft to a teammate an admin marked as a test
 * recipient, or null for a prospect.
 *
 * Approving it sends a real message from a real account to somebody on the
 * team, and spends the same allowance. The row and the pane say so, so nobody
 * approves a test thinking it is outreach, or skips outreach thinking it is a
 * test.
 */
export function teamTestLabel(prospect: { internal?: boolean }): { badge: string; note: string } | null {
  return prospect.internal === true
    ? {
        badge: 'Team test',
        note: 'This goes to a teammate marked as a test recipient. It is a real message from a real account.',
      }
    : null
}

/**
 * The line about the rewrite, and when there is no line.
 *
 * WHY SILENCE IS THE COMMON CASE. The pass runs on every draft, and on a message
 * that already read like a person its correct answer is to change nothing. A line
 * on every row saying so is a line nobody reads by the second day, and worse, it
 * trains somebody to skip the place where the interesting version of it appears.
 * The reviewer's question is not "did a pass run", it is "are these the words the
 * writer chose", and that question only has an answer worth printing when they
 * are not.
 *
 * Three cases, in the order they matter:
 *
 *   - **A mark survived.** Said first and said plainly, because this one is the
 *     reviewer's problem rather than a note: the message in front of them still
 *     carries something the check objected to.
 *   - **It was rewritten.** The rewriter's own sentence, so the reviewer can see
 *     what moved without diffing anything.
 *   - **Nothing changed, or the row is too old to say.** Nothing.
 */
export function humanisedNote(item: Pick<ApprovalItem, 'humanised'>): string | null {
  const record = item.humanised
  if (record === null || record === undefined) return null
  if (record.remaining.length > 0) {
    return 'The check for machine-sounding writing still finds something in this one. Read it before approving.'
  }
  if (!record.ran) return null
  if (!record.changed) return null
  const note = record.note.trim()
  return note.length === 0 ? 'The wording was changed to read less like a machine wrote it.' : `Rewritten: ${note}`
}

/**
 * A step parked because the name on the profile was not the name we hold. The
 * phone typed nothing, so a person either takes the profile's name and sends
 * again, or skips the step.
 */
export const FIX_AND_RETRY_LABEL = 'Fix and retry'
export const SKIP_PARKED_LABEL = 'Skip'
export const SKIP_PARKED_DONE = 'Skipped. Nothing was sent and the sequence moves on.'

/** The ways on from a message held at the moment of sending, said under the reason. */
export const HELD_WAYS_ON =
  'Change the words below and approve them, write it again, or skip this step. Approving it again runs the same check before anything goes out.'
/** Beside the Skip button on a held message. */
export const HELD_SKIP_NOTE = 'Nothing is sent to them for this step and the sequence carries on.'

export function nameMismatchLine(park: { storedName: string; publishedName: string }): string {
  const stored = park.storedName.trim() === '' ? 'is empty' : park.storedName.trim()
  const shown = park.publishedName.trim() === '' ? 'no name' : park.publishedName.trim()
  return `Stored name ${stored}, the profile shows ${shown}`
}

// ---------------------------------------------------------------------------
// Reading a draft in your own language
// ---------------------------------------------------------------------------

/** The two-letter part of a language tag, lowercased, or null. */
function primaryLanguage(code: string | null | undefined): string | null {
  if (code === null || code === undefined) return null
  const head = code.trim().toLowerCase().split(/[-_]/u)[0] ?? ''
  return head === '' ? null : head
}

/**
 * Whether the desk should show a translation beside this draft.
 *
 * The same rule the server applies: only when the words' language is known, the
 * reader has said what they read, and the two differ. Words with no recorded
 * language have it read off the text, which answers only when the text settles
 * it, so English is never put beside English.
 */
export function needsTranslation(
  draftLanguage: string | null | undefined,
  readingLanguage: string | null | undefined,
  words?: string,
): boolean {
  const from = primaryLanguage(draftLanguage) ?? detectTextLanguage(words ?? '')
  const to = primaryLanguage(readingLanguage)
  return from !== null && to !== null && from !== to
}

/** What the translation read returns. */
export interface DraftTranslation {
  status: 'not_needed' | 'not_yet' | 'translated'
  from: string | null
  to: string | null
  subject: string | null
  body: string | null
  cached: boolean
}
